/**
===================================================
Module: frontend.src.contexts.AuthContext.tsx
Description: Authentication context provider for user management (Cognito)
Owner: Kawada Technosystem Co Ltd
All Rights Reserved
This script is proprietary and confidential.
Unauthorized copying, distribution, or modification of this
file, via any medium, is strictly prohibited.
===================================================
*/

import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { User, LoginRequest, RegisterRequest, RegisterResponse, AuthContextType } from "../types";
import { apiService } from "../services/api";

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Initialize auth state
  useEffect(() => {
    const initAuth = async () => {
      if (apiService.isAuthenticated()) {
        try {
          // Import cognito service lazily to avoid circular dependency
          const { cognitoService } = await import("../services/cognito");

          // Validate session with Cognito
          const session = await cognitoService.getSession();
          if (session) {
            // Get user from backend
            const currentUser = await apiService.getCurrentUser();
            setUser(currentUser);
          } else {
            // Session invalid, clean up
            apiService.logout();
          }
        } catch (error) {
          console.error("Auth init error:", error);
          apiService.logout();
        }
      }
      setIsLoading(false);
    };

    initAuth();
  }, []);

  const login = useCallback(async (credentials: LoginRequest) => {
    try {
      await apiService.login(credentials.email, credentials.password);
      const currentUser = await apiService.getCurrentUser();
      setUser(currentUser);
    } catch (error) {
      throw error;
    }
  }, []);

  const register = useCallback(async (data: RegisterRequest): Promise<RegisterResponse> => {
    try {
      const result = await apiService.register(
        data.email,
        data.password,
        data.firstName,
        data.lastName,
        data.phoneNumber
      );
      return result;
    } catch (error) {
      throw error;
    }
  }, []);

  const confirmRegistration = useCallback(async (email: string, code: string) => {
    await apiService.confirmRegistration(email, code);
  }, []);

  const forgotPassword = useCallback(async (email: string) => {
    await apiService.forgotPassword(email);
  }, []);

  const resetPassword = useCallback(async (email: string, code: string, newPassword: string) => {
    await apiService.resetPassword(email, code, newPassword);
  }, []);

  const logout = useCallback(() => {
    apiService.logout();
    setUser(null);
  }, []);

  const refreshUser = useCallback(async () => {
    if (apiService.isAuthenticated()) {
      try {
        const currentUser = await apiService.getCurrentUser();
        setUser(currentUser);
      } catch (error) {
        console.error("Refresh user error:", error);
      }
    }
  }, []);

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    register,
    confirmRegistration,
    forgotPassword,
    resetPassword,
    logout,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
